import React, { useEffect, useMemo, useState } from 'react';
import { MegaphoneIcon, PlusIcon, XIcon } from 'lucide-react';
import { PermissionList } from './PermissionList';
import { PermissionId } from '../data/promotionPermissions';
export function PromotionSettings({
  brand,
  submissionName
}: {
  brand?: string;
  submissionName?: string;
}) {
  const [denied, setDenied] = useState<PermissionId[]>([]);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [draft, setDraft] = useState('');

  // Permissions are per-submission; switching the scope resets the panel.
  useEffect(() => {
    setDenied([]);
    setExcluded([]);
    setDraft('');
  }, [brand, submissionName]);
  const summary = useMemo(() => {
    if (denied.length === 0 && excluded.length === 0) return 'Full permissions granted';
    const count = denied.length + (excluded.length > 0 ? 1 : 0);
    return `${count} ${count === 1 ? 'restriction' : 'restrictions'} applied`;
  }, [denied, excluded]);
  const toggle = (id: PermissionId) => setDenied(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  const addExclusion = () => {
    const value = draft.trim();
    if (!value || excluded.includes(value)) return;
    setExcluded(current => [...current, value]);
    setDraft('');
  };
  return <section className="mt-5" data-id="element-1952" data-mp="cp4x0sfq76kx">
      <article className="rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="promotion-heading" data-id="element-1953" data-mp="cqg00q1ckaaz">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1954" data-mp="10g0jrb5brblb">
          <div data-id="element-1955" data-mp="14fst3q2jllrq">
            <div className="flex items-center gap-2" data-id="element-1956" data-mp="14garas8rma2a">
              <MegaphoneIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1957" data-mp="qhkrv5yh3yar" />
              <h2 id="promotion-heading" className="text-lg font-semibold" data-id="element-1958" data-mp="vg5wdhkrd450">Promotion &amp; brand permissions</h2>
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-1959" data-mp="3b68u3a2iipjm">
              Choose how Carli can promote this inventory and which brand assets can be used. Everything is allowed by default —
              withdraw only what your agreements don&rsquo;t cover.
            </p>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${denied.length > 0 || excluded.length > 0 ? 'border-[#BB5E40]/30 bg-[#BB5E40]/8 text-[#94452E]' : 'border-forest/30 bg-forest/8 text-forest'}`} data-id="element-1960" data-mp="2s3qahtdx01ou">
            {summary}
          </span>
        </div>

        <div className="mt-6" data-id="element-1961" data-mp="28dwmlqebm6pw">
          <PermissionList denied={denied} onToggle={toggle} data-id="element-1962" data-mp="28gsgn3n1sb92" />
        </div>

        <div className="mt-7 border-t border-ink/10 pt-6" data-id="element-1963" data-mp="24dmeo86yx68e">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1964" data-mp="1zej7oi7fgpxs">Product-level exclusions</p>
          <p className="mt-2 text-sm leading-relaxed text-ink/60" data-id="element-1965" data-mp="2kohk065rrhn">
            Hold specific styles, categories, or collaborations out of all promotion while the rest of the upload is marketed
            normally.
          </p>

          {excluded.length > 0 && <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1966" data-mp="a4z4c4j5korl">
              {excluded.map(item => <li key={item} data-id="element-1967" data-mp="ew7dr0khga4r">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#BB5E40]/30 bg-[#BB5E40]/8 py-1.5 pl-3 pr-2 text-xs font-semibold text-[#94452E]" data-id="element-1968" data-mp="ex0dhhl0xzde">
                    {item}
                    <button type="button" onClick={() => setExcluded(current => current.filter(value => value !== item))} aria-label={`Remove ${item} exclusion`} className="rounded-full p-0.5 transition-colors hover:bg-[#BB5E40]/15" data-id="element-1969" data-mp="3idzbkvlavoav">
                      <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-1970" data-mp="17kmr7inyukdz" />
                    </button>
                  </span>
                </li>)}
            </ul>}

          <div className="mt-3 flex flex-wrap gap-2" data-id="element-1971" data-mp="1boaw1tl7r1vj">
            <input value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addExclusion();
            }
          }} placeholder="Style name, SKU, or category" aria-label="Add a product exclusion" className="min-w-[220px] flex-1 rounded-xl border border-ink/15 bg-cream/40 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-1972" data-mp="1a63vthaqb2hf" />
            <button type="button" onClick={addExclusion} disabled={!draft.trim()} className="inline-flex items-center gap-1.5 rounded-xl border border-ink/15 bg-paper px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-[#BB5E40]/40 hover:text-[#94452E] disabled:cursor-not-allowed disabled:opacity-45" data-id="element-1973" data-mp="22vkli76ys59y">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1974" data-mp="2qfagt0qm7itt" />
              Add exclusion
            </button>
          </div>
        </div>

        <p className="mt-6 text-xs leading-relaxed text-ink/50" data-id="element-1975" data-mp="2hh861th2xbtc">
          Permissions apply to {submissionName ?? 'this upload'} only. Carli factors them into every recovery forecast, so
          estimates shown elsewhere already reflect what she is allowed to do.
        </p>
      </article>
    </section>;
}